// Tempo controller for managing playback tempo and synchronization

export interface TempoControllerOptions {
  audioContext: AudioContext;
  initialTempo: number;
  calculatedTempo: number;
}

export interface TempoUpdateFunction {
  (): void;
}

export class TempoController {
  private audioContext: AudioContext;
  private currentTempo: number = 120;
  private calculatedTempo: number = 120;
  private constantSourceNode: ConstantSourceNode | null = null;
  private updateFunctions = new Set<TempoUpdateFunction>();
  private isConnected = false;
  private isStarted = false;

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext;
  }

  /**
   * Initialize the tempo control node
   */
  initialize(): void {
    if (this.constantSourceNode) {
      return; // Already initialized
    }

    const tempoRatio = this.currentTempo / this.calculatedTempo;
    this.constantSourceNode = new ConstantSourceNode(this.audioContext, {
      offset: tempoRatio
    });

    console.log(
      `🎵 Calculated tempo: ${this.calculatedTempo}, desired tempo: ${this.currentTempo}, ratio: ${tempoRatio}`
    );
  }

  /**
   * Connect tempo control to audio worklet parameters
   * @param playbackRate Playback rate AudioParam
   * @param stretchRate Stretch rate AudioParam
   */
  connectToWorklets(playbackRate?: AudioParam, stretchRate?: AudioParam): void {
    if (!this.constantSourceNode) {
      throw new Error('TempoController not initialized');
    }

    if (playbackRate) {
      this.constantSourceNode.connect(playbackRate);
    }

    if (stretchRate) {
      this.constantSourceNode.connect(stretchRate);
    }

    if (playbackRate || stretchRate) {
      this.isConnected = true;
      console.log('🎛️ Tempo control connected');
    }
  }

  /**
   * Start the tempo control (safe to call multiple times)
   */
  start(): void {
    if (!this.constantSourceNode) {
      throw new Error('TempoController not initialized');
    }

    if (this.isStarted) {
      console.log('🎵 TempoController already started, skipping');
      return;
    }

    if (!this.isConnected) {
      console.warn('⚠️ Tempo control not connected to any worklets');
    }

    this.constantSourceNode.start();
    this.isStarted = true;
    console.log('🎵 TempoController started successfully');
  }

  /**
   * Stop the tempo control
   */
  stop(): void {
    if (this.constantSourceNode) {
      try {
        this.constantSourceNode.stop();
      } catch (e) {
        // Ignore if already stopped
      }
      this.constantSourceNode.disconnect();
      this.constantSourceNode = null;
      this.isConnected = false;
      this.isStarted = false;
    }
  }

  /**
   * Get current tempo
   */
  getTempo(): number {
    return this.currentTempo;
  }

  /**
   * Set new tempo
   * @param tempo New tempo in BPM
   */
  setTempo(tempo: number): void {
    this.currentTempo = tempo;
    this.updateTempoRatio();
    this.notifyUpdateFunctions();
  }

  /**
   * Set tempo ratio directly and initialize if needed
   * @param ratio Tempo ratio (desired/original)
   */
  setTempoRatio(ratio: number): void {
    // Initialize if not already done
    if (!this.constantSourceNode) {
      this.initialize();
    }

    // Calculate tempos from ratio
    this.currentTempo = this.calculatedTempo * ratio;

    if (this.constantSourceNode) {
      this.constantSourceNode.offset.value = ratio;
    }
  }

  /**
   * Update the tempo ratio on the constant source node
   */
  private updateTempoRatio(): void {
    if (!this.constantSourceNode) {
      return;
    }

    const newTempoRatio = this.currentTempo / this.calculatedTempo;
    this.constantSourceNode.offset.value = newTempoRatio;
    console.log(`🎵 Updated tempo ratio to: ${newTempoRatio} (tempo: ${this.currentTempo})`);
  }

  /**
   * Register a function to be called when tempo updates
   * @param fn Update function
   */
  registerUpdateFunction(fn: TempoUpdateFunction): void {
    this.updateFunctions.add(fn);
  }

  /**
   * Unregister an update function
   * @param fn Update function to remove
   */
  unregisterUpdateFunction(fn: TempoUpdateFunction): void {
    this.updateFunctions.delete(fn);
  }

  /**
   * Clear all update functions
   */
  clearUpdateFunctions(): void {
    this.updateFunctions.clear();
  }

  /**
   * Notify all registered update functions
   */
  private notifyUpdateFunctions(): void {
    this.updateFunctions.forEach(fn => {
      try {
        fn();
      } catch (error) {
        console.error('Error in tempo update function:', error);
      }
    });
  }

  /**
   * Get the tempo ratio
   */
  getTempoRatio(): number {
    return this.currentTempo / this.calculatedTempo;
  }

  /**
   * Get the calculated (original) tempo
   */
  getCalculatedTempo(): number {
    return this.calculatedTempo;
  }

  /**
   * Create an update function for a specific audio engine instance
   * @returns Update function that updates the tempo ratio
   */
  createUpdateFunction(): TempoUpdateFunction {
    return () => {
      this.updateTempoRatio();
    };
  }

  /**
   * Check if the controller is initialized
   */
  isInitialized(): boolean {
    return this.constantSourceNode !== null;
  }

  /**
   * Check if the controller is connected to worklets
   */
  getIsConnected(): boolean {
    return this.isConnected;
  }
}

/**
 * Calculate tempo from audio parameters
 * @param lengthInBeats Number of beats in the audio
 * @param lengthInSamples Number of samples in the audio
 * @param sampleRate Sample rate of the audio context
 * @returns Calculated tempo in BPM
 */
export function calculateTempo(
  lengthInBeats: number,
  lengthInSamples: number,
  sampleRate: number
): number {
  return (60 * lengthInBeats * sampleRate) / lengthInSamples;
}
